/**
 * BrowserLiveView
 *
 * Renders a browser session's live screencast as an OutputCard, expandable into
 * a PreviewLayover — the same pair the Run Log uses, so a session looks the same
 * wherever it is shown.
 *
 * This is the presentational half: it takes a live view URL that the caller has
 * already resolved. `ChatBrowserSessionWidget` finds that URL by polling an
 * execution's outputs; the `show_browser_session` tool card is handed it
 * directly by the agent. Once the session ends, the screencast endpoint serves
 * the recorded video, so a finished session keeps rendering here with no
 * special-casing.
 *
 * `sidePanel` plays the session in the Browser block of the editor's right
 * rail instead of the transcript, which shows a placeholder with an Open
 * button. Only the focused session plays, to save connections (Chrome allows
 * six per origin); with no rail on screen it plays inline.
 *
 * Other hosts (the Run Log, the outputs sheet, the `set_output` widget) keep
 * playing where they are rendered.
 *
 * The player is one instance in a detached node that is moved between slots,
 * so a move never opens a second SSE connection or restarts the stream.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  cn,
  Icon,
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
  ShimmeringText,
} from '@kognitos/lattice';

import {
  isRecordingUnavailableError,
  streamCache,
  useAgentStream,
  useRecordingMetadata,
} from '@/modules/browser-automation';
import {
  resolveAgentStreamUrl,
  resolveArtifactsBaseUrl,
  resolveScreencastUrl,
} from '@/shared/utils/browser-urls';
import { useTimeInterval } from '@/shared/hooks';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

import {
  claimPrimaryLiveView,
  isPrimaryLiveViewClaimed,
  subscribeToLiveViewClaims,
} from '../live-view-registry';
import {
  focusBrowserPanelSession,
  getBrowserPanelState,
  registerBrowserPanelExpand,
  registerBrowserPanelSession,
  setBrowserPanelSessionStatus,
  subscribeToBrowserPanel,
} from '../browser-panel-registry';
import { useBrowserSessionLabel } from '../hooks/useBrowserSessionLabel';
import { OutputCard } from './OutputCard';
import { BrowserPreviewStatusContext } from './BrowserPreviewStatus';
import { BrowserSessionThumbnail } from './BrowserSessionThumbnail';
import { BrowserRenderer } from './renderers/BrowserRenderer';
import { PreviewLayover } from './PreviewLayover';
import {
  EOutputCardSize,
  ERenderMode,
  type TBrowserPreviewStatus,
  type TResolvedValue,
} from '../types';
import { isFile } from '../utils';

const RECORDING_POLL_INTERVAL_MS = 5_000;

interface ISessionNoticeProps {
  /** What the browser is doing now, or a summary once the session is over. */
  title: string;
  /** The task the browser was given, or the session label. */
  subtitle: string;
  status: TBrowserPreviewStatus;
  /** Keys the title, so each new step animates in. */
  stepKey: number;
  screencastUrl: string | null;
  /** Whether this session is the one playing in the side panel. */
  isInPanel: boolean;
  /** Accessible name for the thumbnail, naming the session and its status. */
  openLabel: string;
  /** Accessible name for the expand button, naming the session. */
  expandLabel: string;
  onOpen: () => void;
  onExpand: () => void;
}

/**
 * Stands in for the player in the transcript: a thumbnail of the session, the
 * step it is on and its task. The thumbnail focuses it in the side panel; the
 * expand button opens it full screen.
 */
function SessionNotice({
  title,
  subtitle,
  status,
  stepKey,
  screencastUrl,
  isInPanel,
  openLabel,
  expandLabel,
  onOpen,
  onExpand,
}: ISessionNoticeProps) {
  const isLive = status === 'live';
  // The card remounts whenever the preview closes; only a new step should animate.
  const [stepKeyOnMount] = useState(stepKey);
  const animatesIn = stepKey !== stepKeyOnMount;
  return (
    <Item
      variant="muted"
      className={cn('gap-4 rounded-xl p-3', isInPanel && 'ring-1 ring-primary')}
      data-testid="browser-live-view-placeholder"
      data-selected={isInPanel ? 'true' : 'false'}
    >
      <ItemMedia>
        <Button
          type="button"
          variant="ghost"
          className="h-auto rounded-lg p-0"
          aria-label={openLabel}
          aria-current={isInPanel || undefined}
          onClick={onOpen}
          data-testid="browser-live-view-open"
        >
          {/* Shares the rail player's stream, so it only follows frames while that plays. */}
          <BrowserSessionThumbnail
            screencastUrl={screencastUrl}
            live={isInPanel && isLive}
          />
        </Button>
      </ItemMedia>
      <ItemContent className="min-w-0 gap-0.5">
        <ItemTitle
          key={stepKey}
          className={cn(
            'block w-full truncate',
            animatesIn &&
              'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 motion-safe:duration-350',
          )}
          data-testid="browser-live-view-title"
        >
          {isLive ? <ShimmeringText text={title} duration={2} /> : title}
        </ItemTitle>
        <ItemDescription
          className="line-clamp-1 text-xs"
          data-testid="browser-live-view-subtitle"
        >
          {subtitle}
        </ItemDescription>
      </ItemContent>
      <ItemActions>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground"
          aria-label={expandLabel}
          onClick={onExpand}
          data-testid="browser-live-view-expand"
        >
          <Icon type="Maximize" size="sm" />
        </Button>
      </ItemActions>
    </Item>
  );
}

interface IBrowserLiveViewProps {
  /** Live view URL as issued by the browser farm (namespace-relative path). */
  liveViewUrl: string;
  /** Card title — the session identifier, or a fallback label. */
  name?: string;
  /** Play in the right rail's Browser block rather than in the transcript. */
  sidePanel?: boolean;
  /** Identifies this tool call in the chat-wide panel selector. */
  panelSessionId?: string;
  /** A newly provisioned Quill browser takes over and opens the panel. */
  autoOpenSidePanel?: boolean;
  /** Ordering clock for competing tool calls. */
  panelSessionStartedAtMs?: number;
  /**
   * Which surface this is when two of them cover the same session.
   *
   * `primary` claims the URL and always renders. `secondary` stands down while
   * a primary holds it, rather than opening a second SSE for identical frames.
   * Defaults to `primary`, so a lone caller is unaffected.
   */
  role?: 'primary' | 'secondary';
}

export function BrowserLiveView({
  liveViewUrl,
  name,
  sidePanel = false,
  panelSessionId,
  autoOpenSidePanel = false,
  panelSessionStartedAtMs,
  role = 'primary',
}: IBrowserLiveViewProps) {
  'use no memo';

  const { t } = useTranslation('browser-automation');

  // Claimed for the component's whole life, so a secondary elsewhere stays
  // stood down even while this one is expanded into the layover.
  // eslint-disable-next-line no-restricted-syntax -- reason: registers in a module registry, with cleanup
  useEffect(() => {
    if (role !== 'primary' || !liveViewUrl) return;
    return claimPrimaryLiveView(liveViewUrl);
  }, [role, liveViewUrl]);

  const isClaimedByPrimary = useSyncExternalStore(
    subscribeToLiveViewClaims,
    () => isPrimaryLiveViewClaimed(liveViewUrl),
  );
  const standDown = role === 'secondary' && isClaimedByPrimary;
  const panelState = useSyncExternalStore(
    subscribeToBrowserPanel,
    getBrowserPanelState,
  );
  const effectiveSessionId = panelSessionId ?? liveViewUrl;
  const isSelected =
    sidePanel && panelState.focusedSessionId === effectiveSessionId;
  const sessionLabel = useBrowserSessionLabel(effectiveSessionId);
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const [previewData, setPreviewData] = useState<{
    name: string;
    value: TResolvedValue;
  } | null>(null);

  const browserValue = useMemo(
    () => ({ browser_name: name ?? 'Browser', live_view_url: liveViewUrl }),
    [name, liveViewUrl],
  );

  const expand = useCallback(
    () =>
      setPreviewData({ name: browserValue.browser_name, value: browserValue }),
    [browserValue],
  );

  // The rail draws the card header, so its expand button reaches back here.
  // eslint-disable-next-line no-restricted-syntax -- reason: registers in a module registry, with cleanup
  useEffect(() => {
    if (!sidePanel) return;
    return registerBrowserPanelExpand(effectiveSessionId, expand);
  }, [sidePanel, effectiveSessionId, expand]);

  // Keeps the last known status when the player unmounts, so a collapsed rail
  // row still shows its badge; only a live player can move it on.
  const reportPanelStatus = useCallback(
    (status: TBrowserPreviewStatus) => {
      if (status) setBrowserPanelSessionStatus(effectiveSessionId, status);
    },
    [effectiveSessionId],
  );

  const [hostEl] = useState(() =>
    typeof document === 'undefined' ? null : document.createElement('div'),
  );
  // State, not a ref: the inline slot is replaced on expand/close, and the
  // effect that parents the player must see it.
  const [inlineSlot, setInlineSlot] = useState<HTMLDivElement | null>(null);

  // Expanded wins: the layover is full-screen and has its own player.
  const isInPanel =
    isSelected &&
    panelState.hasHost &&
    panelState.isOpen &&
    Boolean(panelState.slot) &&
    !previewData;

  // With no rail to move to, the selected chat session plays in its own row.
  const playsInline =
    !previewData && (!sidePanel || (isSelected && !panelState.hasHost));
  const isPlaying = isInPanel || playsInline;

  // A rail row without a player still shows Replay once the recording is
  // finalized: one HTTP read, not a stream. Live needs the stream, so it only
  // comes from a player.
  const knownPanelStatus = panelState.sessionStatuses[effectiveSessionId];
  const artifactsBase = useMemo(
    () => resolveArtifactsBaseUrl(liveViewUrl),
    [liveViewUrl],
  );
  const watchesRecording =
    sidePanel && !isInPanel && knownPanelStatus !== 'replay';
  const {
    data: recordingMetadata,
    error: recordingMetadataError,
    refetch: refetchRecordingMetadata,
  } = useRecordingMetadata(sidePanel ? artifactsBase : null);
  const recordingFinalized =
    Boolean(recordingMetadata?.recording?.filename) &&
    recordingMetadata?.finalized !== false;
  // A row collapsed while Live has no player left to report the end of the
  // session, so it polls until the recording is finalized. Only a permanent
  // failure stops it: the query has no retry, so a transient 5xx would too.
  const { start: startRecordingPoll, stop: stopRecordingPoll } =
    useTimeInterval(() => {
      void refetchRecordingMetadata();
    }, RECORDING_POLL_INTERVAL_MS);
  const pollsRecording =
    watchesRecording &&
    knownPanelStatus === 'live' &&
    !recordingFinalized &&
    !isRecordingUnavailableError(recordingMetadataError);
  // eslint-disable-next-line no-restricted-syntax -- reason: polling lifecycle with cleanup
  useEffect(() => {
    if (!pollsRecording) return;
    startRecordingPoll();
    return stopRecordingPoll;
  }, [pollsRecording, startRecordingPoll, stopRecordingPoll]);
  // eslint-disable-next-line no-restricted-syntax -- reason: writes to a module registry shared with the rail
  useEffect(() => {
    if (recordingFinalized) {
      setBrowserPanelSessionStatus(effectiveSessionId, 'replay');
    }
  }, [recordingFinalized, effectiveSessionId]);

  // Layout effect: registering after paint flashed the placeholder for a frame.
  useLayoutEffect(() => {
    if (!sidePanel) return;
    return registerBrowserPanelSession(effectiveSessionId, {
      startedAtMs: panelSessionStartedAtMs,
      autoFocus: autoOpenSidePanel,
    });
  }, [
    autoOpenSidePanel,
    effectiveSessionId,
    sidePanel,
    panelSessionStartedAtMs,
  ]);

  // eslint-disable-next-line no-restricted-syntax -- reason: imperative API — styles a detached node React does not own
  useEffect(() => {
    if (hostEl) hostEl.style.width = '100%';
  }, [hostEl]);

  const panelSlot = panelState.slot;
  // eslint-disable-next-line no-restricted-syntax -- reason: imperative API — moves the live player between slots without remounting it
  useEffect(() => {
    if (!hostEl) return;
    const target = isInPanel ? panelSlot : playsInline ? inlineSlot : null;
    if (target && hostEl.parentNode !== target) target.appendChild(hostEl);
  }, [isInPanel, playsInline, hostEl, inlineSlot, panelSlot]);

  // Pre-cache agent steps so BrowserStepsViewer has data when expanded.
  const agentStreamUrl = useMemo(() => {
    if (!liveViewUrl || !currentOrganizationId || !currentWorkspaceId)
      return null;
    return resolveAgentStreamUrl(
      liveViewUrl,
      currentOrganizationId,
      currentWorkspaceId,
    );
  }, [liveViewUrl, currentOrganizationId, currentWorkspaceId]);

  // Off while expanded (the layover has its own) or not playing, to save a
  // connection; steps survive in streamCache.
  const { steps: liveSteps } = useAgentStream(
    isPlaying ? agentStreamUrl : null,
  );
  const cachedSteps =
    (agentStreamUrl ? streamCache.get(agentStreamUrl)?.steps : undefined) ?? [];
  const metadataSteps = recordingMetadata?.steps ?? [];
  // Off the stream, the longer list is the newer: the cache stops with the stream, the metadata keeps polling.
  const steps =
    liveSteps.length > 0
      ? liveSteps
      : metadataSteps.length >= cachedSteps.length
        ? metadataSteps
        : cachedSteps;
  const latestStep = steps.at(-1);

  const screencastUrl = useMemo(() => {
    if (!liveViewUrl || !currentOrganizationId || !currentWorkspaceId)
      return null;
    return resolveScreencastUrl(
      liveViewUrl,
      currentOrganizationId,
      currentWorkspaceId,
    );
  }, [liveViewUrl, currentOrganizationId, currentWorkspaceId]);

  // Before the player is rendered, so standing down costs no connection.
  if (!liveViewUrl || standDown) {
    return null;
  }

  // The rail row is the card header (title and expand); controls live in the
  // expanded view.
  const player = isInPanel ? (
    <BrowserPreviewStatusContext value={reportPanelStatus}>
      <BrowserRenderer
        value={browserValue}
        mode={ERenderMode.PREVIEW}
        minimal
      />
    </BrowserPreviewStatusContext>
  ) : (
    <OutputCard
      name={browserValue.browser_name}
      displayName={t('browserAutomation.liveView.title')}
      value={browserValue}
      size={EOutputCardSize.LG}
      onExpand={(cardName, value) => setPreviewData({ name: cardName, value })}
    />
  );

  const renderNotice = () => {
    if (!sidePanel || playsInline) return null;
    const statusLabel =
      knownPanelStatus === 'live'
        ? t('browserAutomation.action.live')
        : knownPanelStatus === 'replay'
          ? t('browserAutomation.video.replayBadge')
          : undefined;
    const title =
      knownPanelStatus === 'replay'
        ? steps.length > 0
          ? t('browserAutomation.liveView.stepsSummary', {
              count: steps.length,
            })
          : t('browserAutomation.liveView.finished')
        : (latestStep?.description ?? t('browserAutomation.liveView.starting'));
    return (
      <SessionNotice
        title={title}
        subtitle={recordingMetadata?.task_description || sessionLabel}
        stepKey={latestStep?.step_number ?? 0}
        screencastUrl={screencastUrl}
        status={knownPanelStatus ?? null}
        isInPanel={isInPanel}
        openLabel={
          statusLabel
            ? t('browserAutomation.liveView.openSessionWithStatus', {
                label: sessionLabel,
                status: statusLabel,
              })
            : t('browserAutomation.liveView.openSession', {
                label: sessionLabel,
              })
        }
        expandLabel={t('browserAutomation.liveView.expandSession', {
          label: sessionLabel,
        })}
        onOpen={() => focusBrowserPanelSession(effectiveSessionId)}
        onExpand={expand}
      />
    );
  };

  return (
    <>
      {/* Unmount OutputCard when expanded: it's invisible behind the
          full-screen PreviewLayover portal and its ScreencastViewer holds
          an SSE connection we don't need while the expanded view has its own.
          On close, OutputCard remounts and restores the last frame from
          frameCache (localStorage) before reconnecting SSE. */}
      {!previewData && (
        <>
          <div
            ref={setInlineSlot}
            className="relative w-full"
            data-testid="browser-live-view-inline"
          >
            {renderNotice()}
          </div>
          {isPlaying && hostEl && createPortal(player, hostEl)}
        </>
      )}
      {previewData && (
        <PreviewLayover
          isOpen
          outputName={previewData.name}
          outputValue={previewData.value}
          onClose={() => setPreviewData(null)}
          showCloseButton={!isFile(previewData.value)}
        />
      )}
    </>
  );
}
